import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { ThreadNote, ThreadResource } from "@/modules/threads/threadModel";
import type { ThreadView } from "@/modules/threads/threadAudit";

const KEY = "note_thread_views";

/** Who saw each thread in view (and when), keyed "resource:id". */
export const useThreadViews = (roots: ThreadNote[]) => {
  const ids = useMemo(() => {
    const byResource: Record<ThreadResource, number[]> = {
      contact_notes: [],
      deal_notes: [],
    };
    for (const root of roots) byResource[root.resource].push(Number(root.id));
    return byResource;
  }, [roots]);

  const { data = [] } = useQuery({
    queryKey: [KEY, ids],
    enabled: ids.contact_notes.length + ids.deal_notes.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const rows: ThreadView[] = [];
      for (const resource of ["contact_notes", "deal_notes"] as const) {
        if (!ids[resource].length) continue;
        const { data: chunk } = await supabase
          .from("note_thread_views")
          .select(
            "note_resource, note_id, member_id, first_viewed_at, last_viewed_at, view_count",
          )
          .eq("note_resource", resource)
          .in("note_id", ids[resource]);
        rows.push(...((chunk ?? []) as ThreadView[]));
      }
      return rows;
    },
  });

  return useMemo(() => {
    const byThread = new Map<string, ThreadView[]>();
    for (const view of data) {
      const key = `${view.note_resource}:${view.note_id}`;
      byThread.set(key, [...(byThread.get(key) ?? []), view]);
    }
    for (const list of byThread.values()) {
      list.sort((a, b) => b.last_viewed_at.localeCompare(a.last_viewed_at));
    }
    return byThread;
  }, [data]);
};

/**
 * Records that the current member saw these threads. Calls are batched
 * (one per second) and each thread is sent at most once per page visit.
 */
export const useMarkThreadsViewed = () => {
  const queryClient = useQueryClient();
  const pending = useRef(new Map<ThreadResource, Set<number>>());
  const sent = useRef(new Set<string>());
  const timer = useRef<number | null>(null);

  const flush = useCallback(async () => {
    timer.current = null;
    const batches = [...pending.current.entries()];
    pending.current.clear();
    for (const [resource, ids] of batches) {
      if (!ids.size) continue;
      await supabase.rpc("mark_note_threads_viewed", {
        p_resource: resource,
        p_note_ids: [...ids],
      });
    }
    await queryClient.invalidateQueries({ queryKey: [KEY] });
  }, [queryClient]);

  useEffect(
    () => () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    [],
  );

  return useCallback(
    (root: ThreadNote) => {
      const key = `${root.resource}:${root.id}`;
      if (sent.current.has(key)) return;
      sent.current.add(key);
      const set = pending.current.get(root.resource) ?? new Set<number>();
      set.add(Number(root.id));
      pending.current.set(root.resource, set);
      if (timer.current == null) {
        timer.current = window.setTimeout(() => void flush(), 1000);
      }
    },
    [flush],
  );
};
